import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FiArchive } from "react-icons/fi";

import "../settings.css";
import SideBar from "../Components/SideBar/SideBar";
import { showSnackBar } from "../../../../../../store/common/snackBarSlice";
import { updateGeneralSettings } from "../../../../../../services/settings.services";
import {
  fetchAutoArchive,
  runAutoArchive,
} from "../../../../../../services/orders.services";
import { addUser } from "../../../../../../store/userSlice";

// The account's general setting for auto-archive: "enabled" or "disabled"
// (none means off), with the days in its meta.
export const AUTO_ARCHIVE = "autoArchiveOrders";
export const DEFAULT_DAYS = 120;
export const MIN_DAYS = 30;
export const MAX_DAYS = 3650;

const plural = (count, word) =>
  `${Number(count).toLocaleString()} ${word}${count === 1 ? "" : "s"}`;

// Whole days in range, or null.
export const validDays = (text) => {
  const value = String(text ?? "").trim();
  if (!/^\d+$/.test(value)) return null;
  const days = Number(value);
  return days >= MIN_DAYS && days <= MAX_DAYS ? days : null;
};

const dateText = (iso, withTime = false) => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    ...(withTime ? { hour: "numeric", minute: "2-digit" } : {}),
  });
};

/**
 * Auto-archive Orders, in Settings: each day the account's fulfilled and
 * cancelled orders older than a number of days (120 unless changed) are
 * archived: they leave the Orders list and keep their status (the Orders
 * page's Archived switch shows them). Off until the account owner switches
 * it on. The page shows how
 * many orders it would archive now, and "Archive now" does it straight
 * away. Only the account owner (a parent user) has it: the settings menu
 * leaves it out for helper users, and this page sends them to their
 * settings.
 */
function AutoArchiveSettings() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const [loaded, setLoaded] = useState(null);
  const [failed, setFailed] = useState(false);
  const [on, setOn] = useState(false);
  const [savedDays, setSavedDays] = useState(DEFAULT_DAYS);
  const [daysText, setDaysText] = useState(String(DEFAULT_DAYS));
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const previewAsked = useRef(0);
  const isOwner = !user?.parent_id;

  const snack = useCallback(
    (text, severity = "success") => dispatch(showSnackBar({ text, severity })),
    [dispatch],
  );

  const show = useCallback((result) => {
    setLoaded(result);
    setOn(Boolean(result?.enabled));
    setSavedDays(result?.days ?? DEFAULT_DAYS);
    setPreview({
      days: result?.preview_days ?? result?.days,
      count: result?.due_count ?? 0,
      cutoff: result?.cutoff,
    });
  }, []);

  const load = useCallback(async () => {
    const response = await fetchAutoArchive();
    if (!response?.data?.success) {
      setFailed(true);
      return null;
    }
    setFailed(false);
    show(response.data.result);
    return response.data.result;
  }, [show]);

  // Loads the setting, and puts its days in the field.
  const reload = useCallback(async () => {
    const result = await load();
    if (result) setDaysText(String(result.days ?? DEFAULT_DAYS));
  }, [load]);

  useEffect(() => {
    if (isOwner) reload();
  }, [isOwner, reload]);

  const days = validDays(daysText);

  // The count follows the days typed (a moment after typing stops).
  useEffect(() => {
    if (!loaded || days === null || days === preview?.days) return undefined;
    const asked = ++previewAsked.current;
    const timer = setTimeout(async () => {
      const response = await fetchAutoArchive(days);
      if (asked !== previewAsked.current || !response?.data?.success) return;
      const result = response.data.result;
      setPreview({
        days: result?.preview_days ?? days,
        count: result?.due_count ?? 0,
        cutoff: result?.cutoff,
      });
    }, 400);
    return () => clearTimeout(timer);
  }, [days, loaded, preview?.days]);

  if (!isOwner) return <Navigate to="/settings/time-zone" replace />;

  // Saves the switch and the days on the account's settings.
  const save = async (nextOn, nextDays) => {
    setSaving(true);
    const response = await updateGeneralSettings({
      setting_type: AUTO_ARCHIVE,
      value: nextOn ? "enabled" : "disabled",
      meta: { days: nextDays },
    });
    setSaving(false);
    if (!response?.data?.success) {
      snack("Couldn't save the setting. Try again.", "error");
      return false;
    }
    setOn(nextOn);
    setSavedDays(nextDays);
    const updatedUser = { ...user, general_settings: response.data.result };
    dispatch(addUser(updatedUser));
    try {
      localStorage.setItem("user", JSON.stringify(updatedUser));
    } catch {
      // The setting is saved; it just isn't kept in this browser.
    }
    return true;
  };

  const toggle = async () => {
    const next = !on;
    const useDays = days ?? savedDays;
    if (await save(next, useDays)) {
      if (days === null) setDaysText(String(useDays));
      snack(
        next
          ? `Finished orders older than ${plural(useDays, "day")} will be archived each day`
          : "Auto-archive is off",
      );
    }
  };

  const saveDays = async () => {
    if (days === null || days === savedDays) return;
    if (await save(on, days))
      snack(`Saved: orders older than ${plural(days, "day")}`);
  };

  const archiveNow = async () => {
    if (days === null) return;
    setRunning(true);
    const response = await runAutoArchive(days);
    setRunning(false);
    setConfirming(false);
    if (!response?.data?.success) {
      snack(
        response?.error?.response?.data?.message ??
          "Couldn't archive the orders. Try again.",
        "error",
      );
      return;
    }
    const count = response.data.result?.archived_count ?? 0;
    snack(
      count ? `${plural(count, "order")} archived` : "No orders to archive",
    );
    const result = await fetchAutoArchive(days);
    if (result?.data?.success) {
      setLoaded(result.data.result);
      setPreview({
        days,
        count: result.data.result?.due_count ?? 0,
        cutoff: result.data.result?.cutoff,
      });
    }
  };

  const counting = days !== null && preview?.days !== days;
  const lastRun = loaded?.last_run_at
    ? `Last run ${dateText(loaded.last_run_at, true)} · ${plural(loaded?.last_run_count ?? 0, "order")} archived`
    : "";

  return (
    <div className="parent_wraper">
      <SideBar active="3_3" />
      <div className="dash_table_wrapper">
        <div className="mb-6 flex items-center justify-between px-1">
          <h2 className="m-0 text-lg font-semibold text-ui-text">
            Auto-archive Orders
          </h2>
        </div>

        <div className="max-w-[560px] rounded-xl border border-ui-border bg-ui-surface p-[16px]">
          <div className="flex items-start gap-[12px]">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-brand-light">
              <FiArchive size={14} className="text-brand" />
            </div>
            <div className="min-w-[0px] flex-1">
              <p
                id="autoArchiveLabel"
                className="m-0 text-sm font-semibold text-ui-text"
              >
                Archive finished orders automatically
              </p>
              <p
                id="autoArchiveHelp"
                className="m-[0px] mt-[4px] text-xs leading-[1.5] text-ui-text-muted"
              >
                Each day, fulfilled and cancelled orders older than the days
                below are archived: they leave the Orders list and keep their
                status. Turn on Archived on the Orders page to see them, or
                search for one by its number. Stock, shipments and charges
                stay as they are.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={on}
              aria-labelledby="autoArchiveLabel"
              aria-describedby="autoArchiveHelp"
              disabled={!loaded || saving}
              onClick={toggle}
              className={`relative mt-[2px] h-[24px] w-[42px] shrink-0 rounded-full border-0 p-[0px] transition-colors disabled:cursor-wait disabled:opacity-60 ${
                on ? "bg-brand" : "bg-ui-border-strong"
              }`}
            >
              <span
                aria-hidden="true"
                className={`absolute top-[3px] h-[18px] w-[18px] rounded-full bg-white shadow-ui-sm transition-[left] ${
                  on ? "left-[21px]" : "left-[3px]"
                }`}
              />
            </button>
          </div>

          {failed && (
            <p
              role="alert"
              className="m-[0px] mt-[12px] rounded-md bg-state-danger-bg px-[10px] py-[8px] text-xs text-state-danger-text"
            >
              Couldn't load the setting.{" "}
              <button
                type="button"
                onClick={reload}
                className="border-0 bg-transparent p-0 font-semibold text-state-danger-text underline"
              >
                Try again
              </button>
            </p>
          )}

          <div className="mt-[14px] border-t border-ui-border-subtle pt-[12px]">
            <form
              className="flex flex-wrap items-center gap-[8px] text-sm text-ui-text"
              onSubmit={(event) => {
                event.preventDefault();
                saveDays();
              }}
            >
              <label htmlFor="autoArchiveDays">Older than</label>
              <input
                id="autoArchiveDays"
                type="number"
                inputMode="numeric"
                min={MIN_DAYS}
                max={MAX_DAYS}
                step={1}
                value={daysText}
                disabled={!loaded}
                aria-invalid={days === null}
                aria-describedby="autoArchiveDaysHint"
                onChange={(event) => setDaysText(event.target.value)}
                className={`h-[36px] w-[90px] rounded-md border bg-ui-surface px-[10px] text-[16px] text-ui-text outline-none focus:border-brand sm:text-sm ${
                  days === null
                    ? "border-state-danger-border"
                    : "border-ui-border"
                }`}
              />
              <span>days</span>
              {days !== null && days !== savedDays && (
                <button
                  type="submit"
                  disabled={saving}
                  className="min-h-[36px] rounded-md border-0 bg-brand px-[12px] text-xs font-semibold text-brand-contrast transition-colors hover:bg-brand-hover disabled:cursor-wait disabled:opacity-60"
                >
                  Save
                </button>
              )}
            </form>
            <p
              id="autoArchiveDaysHint"
              className={`m-[0px] mt-[6px] text-xs ${
                days === null ? "text-state-danger-text" : "text-ui-text-muted"
              }`}
            >
              {days === null
                ? `Between ${MIN_DAYS} and ${MAX_DAYS} days`
                : `${DEFAULT_DAYS} days is about 4 months.`}
            </p>

            <div className="mt-[12px] flex flex-wrap items-center justify-between gap-[10px] rounded-lg bg-ui-surface-sunken px-[12px] py-[10px]">
              <p
                className="m-0 min-w-[0px] flex-1 text-xs leading-[1.5] text-ui-text"
                aria-live="polite"
              >
                {!loaded || days === null ? (
                  " "
                ) : counting ? (
                  "Counting…"
                ) : preview?.count ? (
                  <>
                    <b>{plural(preview.count, "order")}</b> made before{" "}
                    {dateText(preview.cutoff)} would be archived now.
                  </>
                ) : (
                  <>No finished orders are older than {plural(days, "day")}.</>
                )}
              </p>
              {confirming ? (
                <span className="flex shrink-0 items-center gap-[6px]">
                  <button
                    type="button"
                    onClick={() => setConfirming(false)}
                    disabled={running}
                    className="min-h-[36px] rounded-md border border-ui-border bg-ui-surface px-[12px] text-xs font-medium text-ui-text-muted"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={archiveNow}
                    disabled={running}
                    className="min-h-[36px] rounded-md border-0 bg-brand px-[12px] text-xs font-semibold text-brand-contrast disabled:cursor-wait disabled:opacity-60"
                  >
                    {running
                      ? "Archiving…"
                      : `Archive ${plural(preview?.count ?? 0, "order")}`}
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirming(true)}
                  disabled={
                    !loaded || counting || days === null || !preview?.count
                  }
                  className="min-h-[36px] shrink-0 rounded-md border border-ui-border bg-ui-surface px-[12px] text-xs font-semibold text-ui-text transition-colors hover:border-brand hover:text-brand disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Archive now
                </button>
              )}
            </div>
          </div>

          <p className="m-[0px] mt-[12px] border-t border-ui-border-subtle pt-[10px] text-xs text-ui-text-muted">
            {on ? (
              <>
                <b className="text-state-success-text">On.</b> Runs every day
                for orders older than {plural(savedDays, "day")}.
              </>
            ) : (
              <>
                <b className="text-ui-text">Off.</b> Orders stay in the list
                until you archive them.
              </>
            )}
            {lastRun && <span className="block pt-[4px]">{lastRun}</span>}
          </p>
        </div>
      </div>
    </div>
  );
}

export default AutoArchiveSettings;
